import React, { useState } from 'react';
import { FormControl, TextField } from '@mui/material';
import { UserType } from '../../../common/commonTypes';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import LoadingButton from '@mui/lab/LoadingButton';
import { Trans, useTranslation } from 'react-i18next';
import { sendMessage } from '../../../api';

type ContactModalType = {
    user: UserType;
    onCancel: () => void;
    onSuccess: () => void;
    isFromSubscriptionPrompt?: boolean;
};

const ContactModal = (props: ContactModalType) => {
    const { user, onCancel, onSuccess, isFromSubscriptionPrompt } = props;
    const { t, i18n } = useTranslation();
    const maxLength = 600;

    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [message, setMessage] = useState<string>('');
    const [email, setEmail] = useState<string>('');

    const handleSave = async () => {
        setLoading(true);
        setErrorMessage('');

        try {
            await sendMessage(user.id, { email, message, isFromSubscriptionPrompt });
            setLoading(false);
            onSuccess();
        } catch (error: any) {
            console.log('%c Error sending message >>> ', 'background: #333; color: #bada55', error);

            const message =
                error.response.data.message ||
                error.response.data.error ||
                `Can't send message, please try again later`;
            setErrorMessage(message);
            setLoading(false);
        }
    };

    return (
        <Dialog
            fullWidth
            // fullScreen
            open
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    onCancel();
                }
            }}
        >
            <DialogTitle>{t('MODAL_contact_title')}</DialogTitle>
            <DialogContent>
                {Boolean(errorMessage) && (
                    <Alert severity="error" className="mb-4">
                        {errorMessage}
                    </Alert>
                )}

                {isFromSubscriptionPrompt ? (
                    <p className="text-sm mb-4">
                        <b>{t('MODAL_contact_text_0')}</b>
                        <br />
                        <br />
                        {t('MODAL_contact_text_3')}
                    </p>
                ) : (
                    <p className="text-sm mb-4">
                        <b>{t('MODAL_contact_text_1')}</b>
                        <br />
                        <br />
                        <Trans i18nKey="MODAL_contact_text_2" />
                    </p>
                )}

                <FormControl fullWidth size="small" variant="standard">
                    <TextField
                        label={t('MODAL_contact_email_label')}
                        type="email"
                        sx={{ mb: 2 }}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required={false}
                        fullWidth
                    />
                    <TextField
                        label={t('MODAL_contact_message_label')}
                        multiline
                        rows={6}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        required
                        fullWidth
                        slotProps={{
                            // @ts-ignore
                            htmlInput: { maxLength },
                        }}
                        helperText={`${message.length} / ${maxLength}`}
                    />
                </FormControl>
            </DialogContent>
            <DialogActions>
                <LoadingButton onClick={onCancel} disabled={loading}>
                    {t('MODAL_cancel')}
                </LoadingButton>
                <LoadingButton
                    onClick={handleSave}
                    loading={loading}
                    variant="contained"
                    color="success"
                    disabled={message.length < 25 || message.length > maxLength}
                >
                    {t('MODAL_send')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default ContactModal;
